/**
 * A3 -- one impact-tolerance declaration form, wherever it is reached.
 *
 * The form existed, on the IBS page, rendered only for someone the
 * estate records as that IBS's owner. The Resilience Lead is the other
 * person who declares tolerances, and the API has admitted her all
 * along (`require_ibs_owner_or_operator`) -- so every route she was
 * given ended somewhere she could read the problem and not act on it:
 * the TOLERANCE tile and Getting Started at the admin register, the war
 * room at an IBS page that rendered nothing for her.
 *
 * So this is a reachability fix, not a new capability, and the form is
 * extracted rather than reimplemented: two copies of a governance form
 * drift, and the one nobody is looking at drifts first.
 *
 * Drafting is not signing. What this produces is a draft awaiting the
 * CRO, and it says so, because a draft that looks final is worse than
 * no draft.
 */
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "shared/toast/ToastProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSeatHolders } from "shared/api/useSeatHolders";
import { useMe } from "shared/api/useMe";
import { canRunOperations } from "shared/api/authority";
import { Link } from "react-router-dom";

import { api, type ApiError } from "shared/api/client";
import { refusalMessage } from "shared/api/refusalMessage";
import {
  toleranceStateKey,
  useToleranceState,
} from "shared/tolerance/useToleranceState";
import { roleLabel } from "shared/vocab/workflowState";
import { workKeys } from "shared/work/useMyWork";
import { asSentence, toleranceSentence } from "shared/tolerance/toleranceSentence";

/**
 * The declarable dimensions, less `duration_hours` -- that one is the
 * hours field, and offering it twice would let a declaration disagree
 * with itself. Mirrors the closed vocabulary in
 * `shared/tolerance/metrics.py`; a value outside it is ignored on
 * promotion rather than rejected, so this list going stale degrades to
 * a metric nobody can declare, never to a wrong one being enforced.
 */
const DIMENSIONS = [
  "customers_affected",
  "financial_loss_sar",
  "transactions_lost",
  // P417. E-21 names four things a tolerance for disruption includes,
  // and two of them were not offered here at all: diminishment of
  // service and loss of data. Both are MEASURED rather than projected
  // from the outage clock, which is why they are worth declaring --
  // the other three all follow from the same duration.
  "service_degradation_pct",
  "data_loss_hours",
  // P429.3 -- judged on OBSERVED values from tests, drills and
  // incidents, never projected. Declaring one without ever recording an
  // observation reads "cannot be judged", which is the honest answer.
  "integrity_breach_hours",
  "fail_closed_minutes",
] as const;

type Dimension = (typeof DIMENSIONS)[number];

/** P419.1 -- the window kinds P417's matchers accept.
 *
 *  Three of the five are offered here. `daily` and `dated` need a pair
 *  of values rather than one, and a form that asks for two numbers in
 *  one box is how a rule ends up unreadable and silently never
 *  applying. They stay an API act until the calendar editor exists,
 *  which is a smaller gap than having no editor at all. */
const WINDOW_KINDS = ["monthly", "weekly", "annual"] as const;
type WindowKind = (typeof WINDOW_KINDS)[number];

/** Build the structured rule P417 validates on save.
 *
 *  Structured rather than free text for the reason the backend gives:
 *  an expression nobody can evaluate is a rule nobody can rely on. A
 *  value that does not parse produces an EMPTY rule, which the
 *  matchers treat as never applying and the promotion path drops --
 *  so a typo costs the window rather than tightening a tolerance for
 *  a reason nobody can see. */
export function ruleFor(kind: WindowKind, raw: string): Record<string, unknown> {
  const n = Number(raw);
  if (kind === "monthly") {
    return Number.isFinite(n) && n > 0 ? { last_business_days: n } : {};
  }
  if (kind === "weekly") {
    const days = raw
      .split(",")
      .map((d) => Number(d.trim()))
      .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
    return days.length ? { weekdays: days } : {};
  }
  // annual: "12-20..01-05"
  const [from, to] = raw.split("..").map((s) => s.trim());
  const parse = (v: string | undefined) => {
    const [m, d] = (v ?? "").split("-").map((x) => Number(x));
    return Number.isInteger(m) && Number.isInteger(d) ? [m, d] : null;
  };
  const a = parse(from);
  const b = parse(to);
  return a && b
    ? { from_month: a[0], from_day: a[1], to_month: b[0], to_day: b[1] }
    : {};
}

export function ToleranceDeclarationForm({
  ibsId,
  testIdPrefix = "tolerance-declare",
}: {
  ibsId: string;
  testIdPrefix?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  // C1 -- the form asked for a tolerance as though none existed, so an
  // owner who had already declared one declared it again and the estate
  // collected identically named drafts. What is in force, and what is
  // already waiting on a signature, are the two things a reader needs
  // before typing a number.
  const state = useToleranceState();
  const me = useMe();
  // Guarded: a partial payload must degrade to "we do not know what
  // exists" and still let the reader declare, rather than taking the
  // form down with it.
  const existing = Array.isArray(state.data?.rows)
    ? state.data.rows.find((r) => r.ibs_service_id === ibsId)
    : undefined;
  const [hours, setHours] = useState("");
  const [rationale, setRationale] = useState("");
  const [dimension, setDimension] = useState<Dimension | "">("");
  const [threshold, setThreshold] = useState("");
  // P419.1 -- the two things P417 built an engine for and no way to
  // author. Both are OPTIONAL and both travel in this declaration,
  // because a window or a limit approved by another route would be a
  // tolerance nobody signed for.
  const [limit, setLimit] = useState("");
  const [windowLabel, setWindowLabel] = useState("");
  const [windowKind, setWindowKind] = useState<WindowKind>("monthly");
  const [windowHours, setWindowHours] = useState("");
  // Round 14 D6 -- empty, not a monthly default: switching to Annually
  // kept the Monthly "3", which parses to nothing for an annual rule,
  // and the form stored a window that could never apply.
  const [windowRule, setWindowRule] = useState("");

  const seats = useSeatHolders(Boolean(existing?.awaiting_role));
  const declare = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      api.post(`/v1/admin/foundation/ibses/${ibsId}/impact-tolerance-declarations`, {
        declared_tolerance_hours: Number(hours),
        rationale: rationale.trim(),
        // Omitted entirely when not filled in. Sending an empty object
        // would declare a set of no dimensions, which is a different
        // statement from declaring only a duration.
        ...(dimension && Number(threshold) > 0
          ? { declared_metrics: { [dimension]: Number(threshold) } }
          : {}),
        // P419.1: omitted entirely when not filled in, for the same
        // reason the metrics are. Sending an empty list would declare
        // a set of NO windows, which withdraws any that exist -- a
        // different statement from declaring none.
        ...(limit && Number(limit) > 0
          ? { declared_limits: { duration_hours: Number(limit) } }
          : {}),
        ...(windowLabel.trim() && Number(windowHours) > 0
          ? {
              declared_windows: [
                {
                  label: windowLabel.trim(),
                  window_kind: windowKind,
                  rule: ruleFor(windowKind, windowRule),
                  tolerance_hours: Number(windowHours),
                },
              ],
            }
          : {}),
      }),
    onSuccess: () => {
      setHours("");
      setRationale("");
      setDimension("");
      setThreshold("");
      setLimit("");
      setWindowLabel("");
      setWindowHours("");
      void qc.invalidateQueries({ queryKey: ["my-estate"] });
      // C1 -- this invalidated `["tolerance"]`, and React Query matches
      // a key from its START: the shared state key is
      // ["twin","tolerance","state"], so nothing was ever invalidated
      // and the TOLERANCE tile stayed stale until a reload. Both real
      // keys, named.
      void qc.invalidateQueries({ queryKey: toleranceStateKey });
      void qc.invalidateQueries({ queryKey: ["twin", "tolerance-metrics"] });
      // D4 -- and the story hub, which is the chip DIRECTLY ABOVE this
      // form on the same page. It read "not set" for minutes after the
      // owner declared, because C1 named the two keys it knew about and
      // this third one kept its own sixty-second cache.
      void qc.invalidateQueries({ queryKey: ["twin", "ibs-story", ibsId] });
      /* ROUND 13 G8 -- and the inbox built to answer "where did my
         work go". The ADMIN declaration panel has refetched it since
         round 11 D2; this form, which is the one the owner actually
         uses on his own page, never did -- so his declaration did not
         reach his own My Work until some later session refetched it.
         Sixth round for the class, on the other of the two forms. */
      void qc.invalidateQueries({ queryKey: workKeys.mine });
      // D9 -- an inline confirmation and nothing else. Declaring a
      // tolerance hands it to the CRO, and the person who declared it
      // was not told that, or that anything had left his hands.
      toast.push({
        tone: "success",
        message: t("ownerPanel.tolerance.declareSentToCro"),
      });
    },
  });

  // A dimension chosen without a figure is an unfinished thought, so it
  // blocks rather than being silently dropped from the request.
  const dimensionIncomplete = dimension !== "" && !(Number(threshold) > 0);
  // P419.1 -- the same rule, applied to the two new fields.
  //
  // A window named but not given a tighter figure is an unfinished
  // thought, and so is the reverse. And a limit outside the tolerance
  // is refused HERE as well as at promotion, because the server drops
  // it silently and a form that lets you type one and then discards it
  // is a form that lies about what it did.
  const windowIncomplete = windowLabel.trim().length > 0 !== Number(windowHours) > 0;
  const windowNotTighter =
    Number(windowHours) > 0 &&
    Number(hours) > 0 &&
    Number(windowHours) >= Number(hours);
  // Round 14 D6 -- a window that names a rule the matcher cannot read
  // never applies. Refused here rather than stored as a window that
  // reads "declared" on the register and does nothing.
  const windowRuleEmpty =
    windowLabel.trim().length > 0 &&
    Object.keys(ruleFor(windowKind, windowRule)).length === 0;
  // D6 -- and the limit sits inside the TIGHTEST figure, which is the
  // window's where one is declared. A limit equal to the window's hours
  // was accepted, and crossing it would warn at the moment of breach.
  const tightest =
    Number(windowHours) > 0 && Number(windowHours) < Number(hours)
      ? Number(windowHours)
      : Number(hours);
  const limitNotInside = Number(limit) > 0 && tightest > 0 && Number(limit) >= tightest;
  const canDeclare =
    Number(hours) > 0 &&
    rationale.trim().length > 0 &&
    !dimensionIncomplete &&
    !windowIncomplete &&
    !windowNotTighter &&
    !windowRuleEmpty &&
    !limitNotInside;

  // A declaration awaiting a signature is not a reason to write
  // another one. The form stands down and says who has it.
  // Round 14 D6 -- ANY declaration in flight, including one behind a
  // tolerance already in force. The form said "Declaring again replaces
  // it" over a signed figure with a proposal waiting behind it, and
  // the server answered 409, because one ceremony runs at a time.
  if (existing?.status === "pending" || existing?.proposed_hours != null) {
    return (
      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-slate-700">
          {t("ownerPanel.tolerance.label")}
        </label>
        <p
          data-testid={`${testIdPrefix}-pending`}
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
        >
          {(() => {
            /* ROUND 15 G3 -- "Nothing more to do here until they
                   sign it" stayed true for ever on a host where no one
                   held the seat. Name the holder; and when nobody
                   holds it, say so and who can act. */
            const role = existing?.awaiting_role ?? "";
            const holders = seats.data?.holders?.[role];
            const roleName = roleLabel(role);
            // Round 16 G14 -- waiting on an owner who is not recorded.
            if (role === "business_owner" && existing?.owner_recorded === false) {
              // Round 20 W3 -- a named owner is not "none recorded".
              return existing.owner_name
                ? t("ownerPanel.tolerance.pendingOwnerNotSeat", {
                    hours: existing.proposed_hours,
                    owner: existing.owner_name,
                  })
                : t("ownerPanel.tolerance.pendingNoOwner", {
                    hours: existing?.proposed_hours,
                  });
            }
            if (holders !== undefined && holders.length === 0) {
              return t("ownerPanel.tolerance.pendingUnheld", {
                hours: existing?.proposed_hours,
                role: roleName,
              });
            }
            if (holders !== undefined && holders.length === 1) {
              return t("ownerPanel.tolerance.pendingNamed", {
                hours: existing?.proposed_hours,
                who: holders[0],
                role: roleName,
              });
            }
            return t("ownerPanel.tolerance.pending", {
              hours: existing?.proposed_hours ?? "",
              role: roleLabel(existing?.awaiting_role),
            });
          })()}
        </p>
        {/* Round 14 G, ninth round -- her reason, back to her. The CRO
            signing it read it; the person who wrote it never saw it
            again once she pressed Declare. */}
        {existing?.proposed_rationale && (
          <p
            data-testid={`${testIdPrefix}-pending-reason`}
            className="text-xs text-slate-600"
          >
            {t("ownerPanel.tolerance.pendingReason", {
              reason: existing.proposed_rationale,
            })}
          </p>
        )}
      </div>
    );
  }

  /* Round 16 G14 -- the server refuses a declaration on a service with
     no recorded owner (only the owner countersigns), and the form
     offered Declare anyway: the refusal came after the work, and named
     no place to go. Say it first, and where. */
  if (existing?.owner_recorded === false) {
    return (
      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-slate-700">
          {t("ownerPanel.tolerance.label")}
        </label>
        <p
          data-testid={`${testIdPrefix}-no-owner`}
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
        >
          {existing.owner_name
            ? t("ownerPanel.tolerance.ownerNotSeat", {
                name: existing.ibs_name,
                owner: existing.owner_name,
              })
            : t("ownerPanel.tolerance.noOwner", { name: existing.ibs_name })}{" "}
          {canRunOperations(me.data) && (
            <Link
              to="/foundation/admin/ibses"
              data-testid={`${testIdPrefix}-no-owner-link`}
              className="font-medium text-brand-brassDark hover:underline"
            >
              {t("ownerPanel.tolerance.noOwnerLink")}
            </Link>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-slate-700">
        {t("ownerPanel.tolerance.label")}
      </label>
      {/* Stitching review 5.5 -- the form's own constructs, taught once. */}
      <FirstMeetings
        terms={[
          "integrity_tolerance",
          "fail_closed",
          "tolerance_window",
          "tolerance_limit",
        ]}
      />
      {/* What the bank is already held to. Declaring over the top of it
          is legitimate -- tolerances change -- but doing so unknowingly
          is what produced three identical drafts. */}
      {existing && existing.hours !== null && (
        <p data-testid={`${testIdPrefix}-existing`} className="text-xs text-slate-600">
          {/* ROUND 13 G7 -- the STATE in the words every other surface
              uses, and after it the only thing this form adds: what
              declaring again would do to it. */}
          {asSentence(toleranceSentence(t, existing, seats.data))}{" "}
          {existing.signed_by
            ? `${t("ownerPanel.tolerance.existingSigner", {
                signer: existing.signed_by,
              })} `
            : ""}
          {t("ownerPanel.tolerance.existingReplaces")}
        </p>
      )}
      {/* Explained before it is asked for: the reader may not be a
          resilience specialist, and a bare hours box invites a guess. */}
      <p className="text-xs text-slate-600">
        {t("ownerPanel.tolerance.explainer")}{" "}
        {/* ROUND 13 G5 -- "Your figure is a draft until the CRO signs
            it" is a claim about the figure in force, not guidance
            about the form, and it sat four lines under "In force: 2h,
            signed by ...". Where a signed tolerance stands, say what
            declaring again actually does instead. */}
        <span data-testid={`${testIdPrefix}-draft-note`}>
          {existing?.status === "signed"
            ? t("ownerPanel.tolerance.explainerReplaces")
            : existing?.status === "set_unsigned" && existing.hours != null
              ? t("ownerPanel.tolerance.explainerUnsigned", { hours: existing.hours })
              : t("ownerPanel.tolerance.explainerDraft")}
        </span>
      </p>
      <div className="flex gap-2">
        {/* Round 10 F2 -- a placeholder is not a name: it is not read
            as one by every assistive technology and it disappears the
            moment somebody types. These four controls carry no visible
            label, so they carry an explicit one, taken from the same
            catalogue string the placeholder shows. */}
        <input
          data-testid={`${testIdPrefix}-hours`}
          aria-label={t("ownerPanel.tolerance.hours")}
          type="number"
          min={1}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder={t("ownerPanel.tolerance.hours")}
          className="w-28 rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <input
          dir="auto"
          aria-label={t("ownerPanel.tolerance.why")}
          data-testid={`${testIdPrefix}-rationale`}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder={t("ownerPanel.tolerance.why")}
          className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
        />
      </div>
      {/* The second dimension is optional: duration alone is a complete
          tolerance, and demanding more would stop a reader recording
          the one number they are sure of. */}
      {/* Round 19 H56 -- a select is as wide as its longest option, and
          on a narrow phone this one pushed the owner's page sideways.
          It may shrink, and the threshold wraps beneath it. */}
      <div className="flex min-w-0 flex-wrap gap-2">
        <select
          aria-label={t("ownerPanel.tolerance.dimensionLabel")}
          data-testid={`${testIdPrefix}-dimension`}
          value={dimension}
          onChange={(e) => setDimension(e.target.value as Dimension | "")}
          className="min-w-0 max-w-full rounded border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="">{t("ownerPanel.tolerance.dimensionNone")}</option>
          {DIMENSIONS.map((d) => (
            <option key={d} value={d}>
              {t(`toleranceMetrics.metric.${d}`)}
            </option>
          ))}
        </select>
        {dimension !== "" && (
          <input
            aria-label={t("ownerPanel.tolerance.threshold")}
            data-testid={`${testIdPrefix}-threshold`}
            type="number"
            min={1}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder={t("ownerPanel.tolerance.threshold")}
            className="w-40 max-w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        )}
      </div>

      {/* P419.1 -- the operational risk limit, and a tighter window.
          Both optional, both travelling in THIS declaration, because
          either approved by another route would be a tolerance nobody
          signed for. */}
      <div className="space-y-1.5 rounded border border-slate-200 bg-slate-50 p-2">
        <p className="text-[11px] text-slate-600">
          {t("ownerPanel.tolerance.limitAndWindowIntro")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-700" htmlFor={`${testIdPrefix}-limit`}>
            {t("ownerPanel.tolerance.limitLabel")}
          </label>
          <input
            id={`${testIdPrefix}-limit`}
            data-testid={`${testIdPrefix}-limit`}
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder={t("ownerPanel.tolerance.limitPlaceholder")}
            className="w-28 rounded border border-slate-300 px-2 py-1 text-sm"
          />
          {limitNotInside && (
            <span
              data-testid={`${testIdPrefix}-limit-error`}
              className="text-[11px] text-rag-red"
            >
              {tightest < Number(hours)
                ? t("ownerPanel.tolerance.limitNotInsideWindow", { hours: tightest })
                : t("ownerPanel.tolerance.limitNotInside")}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            aria-label={t("ownerPanel.tolerance.windowLabelField")}
            data-testid={`${testIdPrefix}-window-label`}
            value={windowLabel}
            onChange={(e) => setWindowLabel(e.target.value)}
            placeholder={t("ownerPanel.tolerance.windowLabelField")}
            className="w-36 rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <select
            aria-label={t("ownerPanel.tolerance.windowKind")}
            data-testid={`${testIdPrefix}-window-kind`}
            value={windowKind}
            onChange={(e) => {
              setWindowKind(e.target.value as WindowKind);
              // D6 -- a rule typed for one kind means nothing to another.
              setWindowRule("");
            }}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {WINDOW_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`ownerPanel.tolerance.windowKinds.${k}`)}
              </option>
            ))}
          </select>
          <input
            aria-label={t(`ownerPanel.tolerance.windowRule.${windowKind}`)}
            data-testid={`${testIdPrefix}-window-rule`}
            value={windowRule}
            onChange={(e) => setWindowRule(e.target.value)}
            placeholder={t(`ownerPanel.tolerance.windowRule.${windowKind}`)}
            className="w-40 rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            aria-label={t("ownerPanel.tolerance.windowHours")}
            data-testid={`${testIdPrefix}-window-hours`}
            type="number"
            min={1}
            value={windowHours}
            onChange={(e) => setWindowHours(e.target.value)}
            placeholder={t("ownerPanel.tolerance.windowHours")}
            className="w-28 rounded border border-slate-300 px-2 py-1 text-sm"
          />
          {windowRuleEmpty && (
            <span
              data-testid={`${testIdPrefix}-window-rule-error`}
              className="text-[11px] text-rag-red"
            >
              {t("ownerPanel.tolerance.windowRuleEmpty")}
            </span>
          )}
          {windowNotTighter && (
            <span
              data-testid={`${testIdPrefix}-window-error`}
              className="text-[11px] text-rag-red"
            >
              {t("ownerPanel.tolerance.windowNotTighter")}
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        data-testid={`${testIdPrefix}-submit`}
        disabled={!canDeclare || declare.isPending}
        onClick={() => declare.mutate()}
        className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {declare.isPending
          ? t("ownerPanel.tolerance.declaring")
          : t("ownerPanel.tolerance.declare")}
      </button>
      {declare.isSuccess && (
        <p
          data-testid={`${testIdPrefix}-drafted`}
          className="mt-1 text-xs text-emerald-700"
        >
          {t("ownerPanel.tolerance.drafted")}
        </p>
      )}
      {declare.isError && (
        <p data-testid={`${testIdPrefix}-error`} className="mt-1 text-xs text-rose-700">
          {refusalMessage(t, declare.error)}
        </p>
      )}
    </div>
  );
}
